import {
 useState, useEffect,
 JSX
} from 'react';
import {
 X
} from 'lucide-react';
import {
 Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Checkbox
} from 'shared/components/ui/checkbox';
import {
 Page
} from '../types/storyBuilder.types';

interface PageSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (page: Page) => void;
  page: Page;
}

export default function PageSettingsModal({
  isOpen,
  onClose,
  onSave,
  page
}: PageSettingsModalProps): JSX.Element {
  const [formState, setFormState] = useState<Page>(page);

  useEffect(() => {
    setFormState(page);
  }, [page]);

  // Handle changes to the input fields
  const handleInputChange = (field: keyof Page, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Automatically update slug if title is changed and slug is default
    if (field === 'title' && (!formState.slug || formState.slug === 'new-page')) {
      setFormState((prev) => ({
        ...prev,
        slug: value.toString().toLowerCase().replace(/\s+/g, '-'),
        pageTitle: value.toString(),
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formState);
  };

  return (
      <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                  <DialogTitle>Page Configuration</DialogTitle>
                  <Button variant="ghost" size="icon" className="absolute right-4 top-4" onClick={onClose}>
                      <X className="h-4 w-4" />
                  </Button>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                  <div className="grid gap-4">
                      <div className="space-y-2">
                          <Label htmlFor="title">Title</Label>
                          <Input
                id="title"
                value={formState.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter page title"
                required
              />
                      </div>

                      <div className="space-y-2">
                          <Label htmlFor="slug">Slug</Label>
                          <Input
                id="slug"
                value={formState.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                placeholder="enter-page-slug"
                required
              />
                      </div>

                      <div className="space-y-2">
                          <Label htmlFor="layout">Layout</Label>
                          <Select value={formState.layout} onValueChange={(value) => handleInputChange('layout', value)}>
                              <SelectTrigger>
                                  <SelectValue placeholder="Select layout" />
                              </SelectTrigger>
                              <SelectContent>
                                  <SelectItem value="Default Layout">Default Layout</SelectItem>
                                  <SelectItem value="Full Width">Full Width</SelectItem>
                                  <SelectItem value="Sidebar">Sidebar</SelectItem>
                              </SelectContent>
                          </Select>
                      </div>

                      <div className="flex flex-col gap-4">
                          <div className="flex items-center space-x-2">
                              <Checkbox
                  id="isHomePage"
                  checked={formState.isHomePage}
                  onCheckedChange={(checked) => handleInputChange('isHomePage', checked)}
                />
                              <Label htmlFor="isHomePage">Set as Home Page</Label>
                          </div>

                          <div className="flex items-center space-x-2">
                              <Checkbox
                  id="status"
                  checked={formState.status}
                  onCheckedChange={(checked) => handleInputChange('status', checked)}
                />
                              <Label htmlFor="status">Page Status (Active/Inactive)</Label>
                          </div>
                      </div>

                      <div className="space-y-2">
                          <Label htmlFor="pageTitle">Page Title</Label>
                          <Input
                id="pageTitle"
                value={formState.pageTitle}
                onChange={(e) => handleInputChange('pageTitle', e.target.value)}
                placeholder="Enter page title (for SEO)"
              />
                      </div>

                      <div className="space-y-2">
                          <Label htmlFor="pageCssClass">Page CSS Class</Label>
                          <Input
                id="pageCssClass"
                value={formState.pageCssClass}
                onChange={(e) => handleInputChange('pageCssClass', e.target.value)}
                placeholder="Enter CSS class names"
              />
                      </div>

                      <div className="space-y-2">
                          <Label htmlFor="visibility">Visibility</Label>
                          <Select value={formState.visibility} onValueChange={(value) => handleInputChange('visibility', value)}>
                              <SelectTrigger>
                                  <SelectValue placeholder="Select visibility" />
                              </SelectTrigger>
                              <SelectContent>
                                  <SelectItem value="public">Public</SelectItem>
                                  <SelectItem value="private">Private</SelectItem>
                                  <SelectItem value="password-protected">Password Protected</SelectItem>
                              </SelectContent>
                          </Select>
                      </div>
                  </div>

                  <div className="flex justify-end">
                      <Button type="submit" className="bg-primary text-white">
                          Save Changes
                      </Button>
                  </div>
              </form>
          </DialogContent>
      </Dialog>
  );
}